import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import {
  FaCheck,
  FaBolt,
  FaLock,
  FaUndo,
  FaCreditCard,
  FaHeadset,
  FaStar,
  FaRocket,
  FaCrown,
} from 'react-icons/fa';
import {
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_POLICIES,
  LAUNCH_OFFER_FINAL_PRICE,
  getPlanById,
  getLaunchDiscountPercent,
} from '../config/subscriptionPlans';
import './AccountSubscriptionPage.css';

const POLICY_ICONS = [FaCreditCard, FaUndo, FaStar, FaLock];

const PlanCard = ({ plan, selected, onSelect, showAction = true }) => {
  const handleCardClick = () => {
    if (!showAction && plan.enabled) onSelect(plan);
  };

  return (
  <article
    className={`acct-sub-plan${plan.featured ? ' acct-sub-plan--featured' : ''}${!plan.enabled ? ' acct-sub-plan--disabled' : ''}${selected ? ' acct-sub-plan--selected' : ''}`}
    onClick={!showAction ? handleCardClick : undefined}
    onKeyDown={!showAction ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } } : undefined}
    role={!showAction && plan.enabled ? 'button' : undefined}
    tabIndex={!showAction && plan.enabled ? 0 : undefined}
  >
    {plan.featured && <span className="acct-sub-plan-rec">Most Popular</span>}
    <div className="acct-sub-plan-head">
      <h2 className="acct-sub-plan-duration">{plan.duration}</h2>
      <div className="acct-sub-plan-badges">
        {plan.badges?.map((b) => (
          <span
            key={b}
            className={`acct-sub-plan-chip${b === 'Active Plan' ? ' acct-sub-plan-chip--active' : ''}`}
          >
            {b}
          </span>
        ))}
        {plan.badge && <span className="acct-sub-plan-chip">{plan.badge}</span>}
      </div>
    </div>
    <div className="acct-sub-plan-price">
      <strong>Rs {plan.priceLabel}</strong>
      <span className="acct-sub-plan-strike">{plan.originalPrice}</span>
      <span>{plan.period}</span>
    </div>
    <ul className="acct-sub-plan-features">
      {plan.features.map((text) => (
        <li key={text}>
          <span className="acct-sub-check" aria-hidden><FaCheck /></span>
          {text}
        </li>
      ))}
    </ul>
    {showAction && (
      <button
        type="button"
        className={`acct-sub-plan-btn${plan.featured ? ' acct-sub-plan-btn--solid' : ' acct-sub-plan-btn--outline'}`}
        disabled={!plan.enabled}
        onClick={() => onSelect(plan)}
      >
        {plan.active && <FaCrown aria-hidden />}
        {plan.buttonLabel}
      </button>
    )}
  </article>
  );
};

const AccountSubscriptionPage = ({ onClose }) => {
  const [view, setView] = useState('plans');
  const [selectedPlanId, setSelectedPlanId] = useState('advance');
  const [subscribed, setSubscribed] = useState(false);

  const selectedPlan = getPlanById(selectedPlanId);
  const discountPct = getLaunchDiscountPercent(selectedPlan);

  const handlePlanSelect = (plan) => {
    if (!plan.enabled) return;
    setSelectedPlanId(plan.id);
    setView('checkout');
  };

  const handleSubscribe = () => {
    setSubscribed(true);
  };

  const launchBanner = (
    <div className="acct-sub-launch">
      <div className="acct-sub-launch-inner">
        <div className="acct-sub-launch-left">
          <span className="acct-sub-launch-badge">
            <FaRocket aria-hidden /> LAUNCHING OFFER
          </span>
          <span className="acct-sub-launch-title">
            <em>100% OFF</em> — Limited time access for everyone
          </span>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    <div className="acct-sub-page acct-sub-page--overlay" role="dialog" aria-label="Account & Subscription">
      <button type="button" className="acct-sub-close" onClick={onClose} aria-label="Close">
        ✕
      </button>

      {view === 'checkout' && (
        <button
          type="button"
          className="acct-sub-back"
          onClick={() => setView('plans')}
          aria-label="Back to plans"
        >
          ← Back
        </button>
      )}

      {launchBanner}

      <div className="acct-sub-wrap">
        {view === 'plans' ? (
          <>
            <header className="acct-sub-intro">
              <span className="acct-sub-kicker">Transparent Pricing</span>
              <h1 className="acct-sub-hero-title">
                Pick a plan that grows with your travel community
              </h1>
              <p className="acct-sub-hero-sub">
                Simple monthly options, smarter long-term savings. Choose a plan and get instant
                access to premium booking tools, tailored support, and launch offers.
              </p>
              <div className="acct-sub-pills">
                <span className="acct-sub-pill"><FaBolt aria-hidden /> Instant activation</span>
                <span className="acct-sub-pill"><FaLock aria-hidden /> Secure payments</span>
                <span className="acct-sub-pill"><FaUndo aria-hidden /> Cancel anytime</span>
              </div>
            </header>

            <div className="acct-sub-plans">
              {SUBSCRIPTION_PLANS.map((plan) => (
                <PlanCard key={plan.id} plan={plan} onSelect={handlePlanSelect} />
              ))}
            </div>

            <section className="acct-sub-policies">
              <h2 className="acct-sub-policies-title">Pricing policies</h2>
              <p className="acct-sub-policies-sub">
                Simple rules so you always know what you are paying for.
              </p>
              <div className="acct-sub-trust">
                {SUBSCRIPTION_POLICIES.map(({ title, lines }, i) => {
                  const Icon = POLICY_ICONS[i];
                  return (
                    <div key={title} className="acct-sub-trust-item">
                      <div className="acct-sub-trust-icon"><Icon aria-hidden /></div>
                      <div>
                        <strong>{title}</strong>
                        {lines.map((line) => <p key={line}>{line}</p>)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="acct-sub-support">
              <div className="acct-sub-support-icon" aria-hidden>💬</div>
              <div>
                <h3>Need help choosing the right plan?</h3>
                <p>
                  Our onboarding team can recommend the best subscription based on your team size
                  and usage.
                </p>
                <div className="acct-sub-support-tags">
                  <span><FaHeadset aria-hidden /> Live chat support</span>
                  <span><FaBolt aria-hidden /> Quick onboarding</span>
                </div>
              </div>
              <button
                type="button"
                className="acct-sub-support-btn"
                onClick={() => { window.location.href = '/feedback'; }}
              >
                Contact support
              </button>
            </section>
          </>
        ) : (
          <>
            <header className="acct-sub-intro">
              <span className="acct-sub-kicker">Secure Checkout</span>
              <h1 className="acct-sub-hero-title">Complete your subscription</h1>
              <p className="acct-sub-hero-sub">
                Review your plan and confirm payment. Launch offer applies automatically at checkout.
              </p>
            </header>

            <div className="acct-sub-checkout-layout">
              <div className="acct-sub-plans acct-sub-plans--checkout">
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    selected={plan.id === selectedPlanId}
                    showAction={false}
                    onSelect={(p) => p.enabled && setSelectedPlanId(p.id)}
                  />
                ))}
              </div>

              <aside className="acct-sub-checkout-panel">
                <div className="acct-sub-checkout-panel-head">
                  <h2>{selectedPlan.duration}</h2>
                  <span className="acct-sub-checkout-offer-badge">{discountPct}% OFF</span>
                </div>

                <div className="acct-sub-checkout-rows">
                  <div className="acct-sub-checkout-row">
                    <span>Plan price</span>
                    <span>Rs {selectedPlan.priceLabel}</span>
                  </div>
                  <div className="acct-sub-checkout-row">
                    <span>Original price</span>
                    <span className="acct-sub-plan-strike">{selectedPlan.originalPrice}</span>
                  </div>
                  <div className="acct-sub-checkout-row acct-sub-checkout-row--offer">
                    <span>Launch offer</span>
                    <span>100% OFF</span>
                  </div>
                </div>

                <div className="acct-sub-checkout-final">
                  <span className="acct-sub-checkout-final-label">Final price</span>
                  <strong className="acct-sub-checkout-final-price">
                    Rs {LAUNCH_OFFER_FINAL_PRICE}
                  </strong>
                </div>

                {subscribed ? (
                  <button type="button" className="acct-sub-plan-btn acct-sub-plan-btn--success" disabled>
                    ✓ Subscribed
                  </button>
                ) : (
                  <button
                    type="button"
                    className="acct-sub-plan-btn acct-sub-plan-btn--solid"
                    onClick={handleSubscribe}
                  >
                    <FaCrown aria-hidden /> Subscribe Now
                  </button>
                )}
                <button type="button" className="acct-sub-plan-btn acct-sub-plan-btn--outline acct-sub-checkout-cancel" onClick={onClose}>
                  Cancel
                </button>
              </aside>
            </div>
          </>
        )}
      </div>

      {subscribed && (
        <div className="acct-sub-success-overlay" role="alertdialog" aria-label="Subscription Successful">
          <div className="acct-sub-success-card">
            <div className="acct-sub-success-icon">✓</div>
            <h3>Subscription Successful</h3>
            <p>Your {selectedPlan.duration} premium plan is now active.</p>
            <button type="button" className="acct-sub-plan-btn acct-sub-plan-btn--solid" onClick={onClose}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default AccountSubscriptionPage;
